import React, {useEffect, useMemo, useRef, useState} from "react";
import {KPIGrid} from "./KPIGrid/KPIGrid";
import dayjs from "dayjs";
import {Accordion, AccordionDetails, AccordionSummary, Container,} from "@mui/material";
import "./ProductDetail.scss";
import {Chart as ChartJS, registerables} from "chart.js";
import {Model, isEmpty} from "weed-js";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {
    CanonicalIntegrationAccount,
    CanonicalInventory, CanonicalProduct,
    CanonicalTransactionItem
} from "../../../../../../models/canonical.js";
import Loading from "../../../../../../components/elements/Loading";
import ProductSummary from "./ProductSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {DutchieUtils, MetrcUtils} from "./utils";
import {METRC, DUTCHIE} from "../../../../../../constants";
import {MetrcAccount} from "../../../../../../models/metrc";
import {DutchieAccount} from "../../../../../../models/dutchie";
import {Client} from "weed-js";


ChartJS.register(...registerables);


const LoadingMessage = ({numLoading, total}) => {
    return <span style={{fontSize: "20px"}}>
        {total === 0 ? 0 : `${( 100 * (total - numLoading) / total).toFixed(0)}% complete`}
    </span>
}


const ProductDetail = ({subscriptions, subscriptionItems, subscriptionProducts}) => {
    const {
        organizationId: _organizationId,
        productId: _productId,
        integration: _integration
    } = useParams();

    const selectedIntegration = _integration;
    const productId = parseInt(_productId);

    const timeRangeAbortControllerRef = useRef(null);
    const transactionAbortControllerRef = useRef(null);
    const selectedIntegrationAccounts = useSelector(state => state.integrationReducer.accounts.selectedAccounts) || [];
    const [currentProduct, setCurrentProduct] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [inventory, setInventory] = useState([]);
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
    const fifteenDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 15);

    const defaultStart = dayjs(thirtyDaysAgo);
    const defaultMiddle = dayjs(fifteenDaysAgo);
    const defaultEnd = dayjs(today);
    const [inProgress, setInProgress] = useState(0);
    const [total, setTotal] = useState(0);
    const clientRef = useRef(new Client());

    const [userDefinedWindow, setUserDefinedWindow] = useState({
        previous: {start: null, end: null},
        current: {start: null, end: null}
    });

    const [activeWindow, setActiveWindow] = useState({
        previous: {start: null, end: null},
        current: {start: null, end: null}
    });

    if (selectedIntegration === DUTCHIE) {
        selectedIntegrationAccounts.map(account => {
            let existing = DutchieAccount.byId.get(account.id);
            if (!existing) existing = new DutchieAccount(account);
            const canonicalExisting = CanonicalIntegrationAccount.byId.get(account.id);
            if (!canonicalExisting) CanonicalIntegrationAccount.fromDutchie(existing);
        });
    } else if (selectedIntegration === METRC) {
        selectedIntegrationAccounts.map(account => {
            let existing = MetrcAccount.byId.get(account.id);
            if (!existing) existing = new MetrcAccount(account);
            const canonicalExisting = CanonicalIntegrationAccount.byId.get(account.id);
            if (!canonicalExisting) CanonicalIntegrationAccount.fromMetrc(existing);
        });
    }

    useEffect(() => {
        if (isEmpty(productId) || isEmpty(selectedIntegrationAccounts)) {
            return;
        }
        if (activeWindow.current.start !== null) {
            return;
        }

        timeRangeAbortControllerRef.current?.abort(); // cancel previous request
        timeRangeAbortControllerRef.current = new AbortController();

        setIsLoading(true);

        const utils = selectedIntegration === DUTCHIE ? DutchieUtils : MetrcUtils;
        utils.getActiveTimeRange({
            client: clientRef.current,
            productId: productId,
            defaultStart,
            defaultMiddle,
            defaultEnd,
            abortController: timeRangeAbortControllerRef?.current
        }).then(([start, middle, end]) => {
            setIsLoading(false);
            setActiveWindow({
                previous: {start: dayjs(start), end: dayjs(middle)},
                current: {start: dayjs(middle), end: dayjs(end)}
            });

        }).catch((err) => {
            console.error(err);
            setIsLoading(false);
        });
        return () => {
            timeRangeAbortControllerRef?.current?.abort();
        }
    }, [
        productId,
        selectedIntegrationAccounts,
    ]);

    useEffect(() => {
        if (isEmpty(productId) || isEmpty(selectedIntegrationAccounts)) {
            return;
        }
        if (activeWindow.previous.start === null || activeWindow.previous.end === null) {
            return;
        }
        if (activeWindow.current.start === null || activeWindow.current.end === null) {
            return;
        }

        transactionAbortControllerRef.current?.abort(); // cancel previous request if any
        transactionAbortControllerRef.current = new AbortController();

        setIsLoading(true);
        setInProgress(0);
        const utils = selectedIntegration === DUTCHIE ? DutchieUtils : MetrcUtils;
        utils.getTransactionsForProductForWindow({
            client: clientRef.current,
            productId,
            activeWindow,
            abortControllerRef: transactionAbortControllerRef,
            integrationAccountIds: selectedIntegrationAccounts.map(ia => ia.id),
            setTotal,
            setIsLoading,
            setInProgress
        }).then(({
            product: fetchedProduct,
            items: fetchedItems,
            inventory: fetchedInventory,
        }) => {
            return Model.canonicalize().then(() => {
                if (!isEmpty(fetchedProduct) && currentProduct?.id !== fetchedProduct?.id) {
                    setCurrentProduct(CanonicalProduct.from(fetchedProduct));
                }
                setIsLoading(false);
                setItems(fetchedItems.map(item => {
                    if (isEmpty(item)) {
                        return null;
                    }
                    return CanonicalTransactionItem.from(item);
                }).filter(item => item !== null));
                setInventory(fetchedInventory.map(inv => CanonicalInventory.from(inv)));
            });
        }).catch(err => {
            console.log("Error fetching transactions for product detail");
            console.error(err);
            setIsLoading(false);
            setInProgress(0);
        });

        return () => {
            transactionAbortControllerRef?.current?.abort();
        };

    }, [
        productId,
        selectedIntegrationAccounts,
        activeWindow
    ]);

    const inventoryByProductId = useMemo(() => {
        return inventory.reduce((acc, inv) => {
            acc[inv.product_id] = inv;
            return acc;
        }, {})
    }, [inventory]);


    const getPercentComplete = (inProgress, total) => {
        if (total === 0) {
            return "";
        }
        const percent = (100 * (total - inProgress) / total).toFixed(0);
        return `${percent}%`;
    }

    if (isLoading) {
        return <Loading title={"Finding Transactions " + getPercentComplete(inProgress, total)}/>;
    }

    if (isEmpty(currentProduct)) {
        return <Container className="product-detail workspace">
            <Accordion onClick={() => {
                setExpanded(!expanded);
            }}
            className={"accordion-no-shadow"}
            >
                <AccordionSummary
                    expandIcon={expanded ? <ExpandLessIcon className={"detail-expand"}/> : <ExpandMoreIcon className={"detail-expand"}/>}
                    aria-controls={"menu-content"}
                    sx={{
                        flexDirection: "row-reverse",         // move expand icon to left
                        "& .MuiAccordionSummary-expandIconWrapper": {
                            mr: 2,                               // add right margin to icon
                        }
                    }}
                >
                    <h1>Product Detail</h1>
                </AccordionSummary>
                <AccordionDetails>
                    <ProductSummary desiredProduct={null} selectedIntegrationAccounts={selectedIntegrationAccounts}/>
                </AccordionDetails>
            </Accordion>
            <div style={{marginTop: "20px"}}>
                <h2>No product selected or product not found</h2>
                <p>Please go back and select a product from the product list.</p>
            </div>
        </Container>
    }

    return <Container className="product-detail workspace">
        <Accordion onClick={() => {
            setExpanded(!expanded);
        }}
        className={"accordion-no-shadow"}
        >
            <AccordionSummary
                expandIcon={expanded ? <ExpandLessIcon className={"detail-expand"}/> : <ExpandMoreIcon className={"detail-expand"}/>}
                aria-controls={"menu-content"}
                sx={{
                    flexDirection: "row-reverse",         // move expand icon to left
                    "& .MuiAccordionSummary-expandIconWrapper": {
                        mr: 2,                               // add right margin to icon
                    }
                }}
            >
                <h1>Product Detail {currentProduct ? `- ${currentProduct.name}` : ""}</h1>
            </AccordionSummary>
            <AccordionDetails>
                {selectedIntegration === DUTCHIE ? <ProductSummary desiredProduct={currentProduct} selectedIntegrationAccounts={selectedIntegrationAccounts}/> : null}
            </AccordionDetails>
        </Accordion>
        <KPIGrid
            activeWindow={activeWindow}
            binSizeDays={1}
            inventoryByProductId={inventoryByProductId}
            product={currentProduct}
            setActiveWindow={setActiveWindow}
            setUserDefinedWindow={setUserDefinedWindow}
            transactionItems={items}
            userDefinedWindow={userDefinedWindow}
            defaultStart={defaultStart}
            defaultMiddle={defaultMiddle}
            defaultEnd={defaultEnd}
        />
    </Container>
};


export default ProductDetail;